import React, { useState } from 'react';
import { Box, Container, Typography, Button, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from '../components/Icons';
import Layout from '../components/Layout';
import ContactFormDialog from '../components/ContactFormDialog';
import { PRODUCT_URLS } from '../constants';

const Systems: React.FC = () => {
  const navigate = useNavigate();
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <Layout>
      <Box
        sx={{
          background:
            'radial-gradient(ellipse 80% 45% at 50% -5%, rgba(14,165,233,0.1), transparent 55%)',
        }}
      >
        <Container maxWidth="lg" sx={{ pt: { xs: 8, md: 11 }, pb: 4 }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'primary.dark',
              mb: 2,
            }}
          >
            Products
          </Typography>
          <Typography variant="h1" sx={{ maxWidth: 820, mb: 2.5, fontSize: { xs: '2.4rem', md: '3.5rem' } }}>
            Wellness for people, tools for clinics, experts underneath.
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 18, maxWidth: 620 }}>
            Companion first, then iPOS, with AdamCore diagnostic experts powering both.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <ProductBlock
          kicker="Wellness Companion"
          title="Companion"
          body="More than a regular health app — a wellness product for the whole person. It uses our proprietary Eat–Move–Care–Restore wellness model (not an AI model) to track and manage holistic wellness: what you are Eating, how well you are Moving, the practices you use to Care for physical and mental health, and how you Restore body and mind for longevity and a fulfilling life. Adam AI and AdamCore sit underneath where diagnostics help."
          points={[
            'Eat–Move–Care–Restore proprietary wellness model',
            'Tracks eat, move, care, and restore together',
            'AdamCore experts when risk insight is needed',
            'Base for medical-aid member apps',
          ]}
          href={PRODUCT_URLS.companion}
          cta="Open Companion"
        />

        <Box sx={{ height: { xs: 48, md: 80 } }} />

        <ProductBlock
          kicker="Clinical platform"
          title="iPOS"
          body="Intelligent Point of Service — clinics get records, scheduling, and AdamCore diagnostics in one place."
          points={[
            'AdamCore experts in the provider workflow',
            'Electronic wellness records',
            'Scheduling and waiting room',
            'Population analytics',
          ]}
          href={PRODUCT_URLS.ipos}
          cta="Open iPOS"
          reverse
        />

        <Box sx={{ height: { xs: 48, md: 80 } }} />

        <Box sx={cardSx}>
          <Typography sx={kickerSx}>Experts</Typography>
          <Typography variant="h2" sx={{ mb: 2 }}>
            AdamCore
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: 3, maxWidth: 720, fontSize: 16 }}>
            Specialist diagnostic experts — not a single general model. Each expert is trained for
            a condition (type 2 diabetes, hypertension, coronary risk, and others in progress) and
            routed through the AdamCore conductor. Already live inside Companion and iPOS.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.5,
              mb: 3,
            }}
          >
            {[
              'Type 2 diabetes risk',
              'Hypertension risk',
              'Coronary atherosclerosis risk',
              'More experts in development',
            ].map((item) => (
              <Box key={item} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', mt: '8px', flexShrink: 0 }} />
                <Typography sx={{ fontSize: 15 }}>{item}</Typography>
              </Box>
            ))}
          </Box>
          <Typography sx={{ color: 'text.secondary', fontSize: 14, maxWidth: 720 }}>
            Down the line we plan developer API access so other teams can try AdamCore as AdamHealth
            grows.
          </Typography>
        </Box>
      </Container>

      <Box sx={{ bgcolor: '#f8fafc', borderTop: '1px solid rgba(15,23,42,0.06)', borderBottom: '1px solid rgba(15,23,42,0.06)' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
          <Typography variant="h2" sx={{ mb: 1.5 }}>
            Member applications
          </Typography>
          <Typography sx={{ color: 'text.secondary', maxWidth: 640, mb: 5, fontSize: 17 }}>
            Branded builds of Companion for medical aids — keep what they paid for, drop the
            rest.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2.5,
            }}
          >
            <Box sx={cardSx}>
              <Typography sx={kickerSx}>Signed</Typography>
              <Typography variant="h4" sx={{ mb: 1 }}>
                ParksMed
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                Member app based on Companion, limited to ParksMed-supported capabilities.
              </Typography>
            </Box>
            <Box sx={cardSx}>
              <Typography sx={kickerSx}>In progress</Typography>
              <Typography variant="h4" sx={{ mb: 1 }}>
                Generation Health
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                Next medical-aid member app on the same foundation.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
            gap: 2.5,
          }}
        >
          <Box sx={cardSx}>
            <Typography sx={kickerSx}>Also</Typography>
            <Typography variant="h4" sx={{ mb: 1.5 }}>
              ziPOS
            </Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              Retail point-of-sale system we built for shops and service businesses.
            </Typography>
          </Box>
          <Box sx={cardSx}>
            <Typography sx={kickerSx}>Hardware</Typography>
            <Typography variant="h4" sx={{ mb: 1.5 }}>
              Shop
            </Typography>
            <Typography sx={{ color: 'text.secondary', mb: 2 }}>
              Devices and kits available with our software.
            </Typography>
            <Button onClick={() => navigate('/shop')} endIcon={<ArrowRight />}>
              Open shop
            </Button>
          </Box>
          <Box sx={cardSx}>
            <Typography sx={kickerSx}>Partners</Typography>
            <Typography variant="h4" sx={{ mb: 1.5 }}>
              Get in touch
            </Typography>
            <Typography sx={{ color: 'text.secondary', mb: 2 }}>
              AdamCore, a member app, clinic rollout, or something outside healthcare.
            </Typography>
            <Button variant="outlined" onClick={() => setContactOpen(true)}>
              Contact
            </Button>
          </Box>
        </Box>
      </Container>

      <Container maxWidth="md" sx={{ pb: { xs: 10, md: 14 }, textAlign: 'center' }}>
        <Typography variant="h2" sx={{ mb: 2 }}>
          Try the live products
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowUpRight />}
            component="a"
            href={PRODUCT_URLS.companion}
            target="_blank"
            rel="noopener noreferrer"
          >
            Companion
          </Button>
          <Button
            variant="outlined"
            size="large"
            endIcon={<ArrowUpRight />}
            component="a"
            href={PRODUCT_URLS.ipos}
            target="_blank"
            rel="noopener noreferrer"
          >
            iPOS
          </Button>
          <Button size="large" onClick={() => setContactOpen(true)}>
            Contact
          </Button>
        </Stack>
      </Container>

      <ContactFormDialog
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        title="Product enquiry"
        description="AdamCore, Companion, iPOS, a member app, ziPOS, or something else."
      />
    </Layout>
  );
};

const cardSx = {
  p: { xs: 3, md: 4 },
  borderRadius: 4,
  border: '1px solid rgba(15,23,42,0.08)',
  bgcolor: '#fff',
};

const kickerSx = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'primary.dark',
  mb: 1.5,
};

const ProductBlock: React.FC<{
  kicker: string;
  title: string;
  body: string;
  points: string[];
  href: string;
  cta: string;
  reverse?: boolean;
}> = ({ kicker, title, body, points, href, cta, reverse }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
      gap: { xs: 4, md: 8 },
      alignItems: 'center',
    }}
  >
    <Box sx={{ order: { xs: 1, md: reverse ? 2 : 1 } }}>
      <Typography sx={kickerSx}>{kicker}</Typography>
      <Typography variant="h2" sx={{ mb: 2 }}>
        {title}
      </Typography>
      <Typography sx={{ color: 'text.secondary', mb: 3, fontSize: 16 }}>{body}</Typography>
      <Stack spacing={1.1} sx={{ mb: 3.5 }}>
        {points.map((p) => (
          <Box key={p} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', mt: '8px', flexShrink: 0 }} />
            <Typography sx={{ fontSize: 15 }}>{p}</Typography>
          </Box>
        ))}
      </Stack>
      <Button
        variant="contained"
        size="large"
        endIcon={<ArrowUpRight />}
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {cta}
      </Button>
    </Box>
    <Box sx={{ order: { xs: 2, md: reverse ? 1 : 2 } }}>
      <Preview label={title} />
    </Box>
  </Box>
);

const Preview: React.FC<{ label: string }> = ({ label }) => {
  const isApp = label.includes('Companion');
  return (
    <Box
      sx={{
        borderRadius: 4,
        border: '1px solid rgba(15,23,42,0.08)',
        overflow: 'hidden',
        bgcolor: '#fff',
        minHeight: 280,
        boxShadow: '0 20px 50px rgba(15,23,42,0.06)',
      }}
    >
      <Box sx={{ height: 42, px: 2, display: 'flex', alignItems: 'center', borderBottom: '1px solid rgba(15,23,42,0.06)' }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>{label}</Typography>
      </Box>
      <Box sx={{ p: 3 }}>
        {(isApp
          ? [
              ['Eat', 'Tracked'],
              ['Move', 'Tracked'],
              ['Care · Restore', 'Active'],
              ['AdamCore', 'Ready'],
            ]
          : [
              ['Queue', '4 waiting'],
              ['Diagnostics', 'AdamCore'],
              ['Consultation', 'Recording'],
              ['Panel risk', 'Stratified'],
            ]
        ).map(([k, v]) => (
          <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', py: 1.1, borderBottom: '1px solid rgba(15,23,42,0.05)' }}>
            <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>{k}</Typography>
            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{v}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default Systems;
